import { createApi, type BaseQueryFn } from '@reduxjs/toolkit/query/react';
import { graphqlRequestBaseQuery } from '@rtk-query/graphql-request-base-query';

export type GqlArgs = {
  document: string;
  /** `void` is allowed so generated endpoints with all-optional variables fit. */
  variables?: Record<string, unknown> | void;
};

export type SessionTokens = {
  accessToken: string | null;
  refreshToken: string | null;
};

export interface CreateGraphqlBaseApiOptions<
  ReducerPath extends string,
  TagType extends string,
> {
  /** Absolute GraphQL endpoint. */
  url: string;
  reducerPath: ReducerPath;
  tagTypes: readonly TagType[];
  /** Mutation document that exchanges a refresh token for a new token pair. */
  refreshMutation: string;
  /** Root field of `refreshMutation` holding the new token pair. */
  refreshPayloadKey: string;
  /** Reads the current token pair out of the host app's store state. */
  getTokens: (state: unknown) => SessionTokens;
  /** Action creator dispatched with the refreshed token pair. */
  setTokens: (tokens: { accessToken: string; refreshToken: string }) => unknown;
  /** Action creator dispatched when the session cannot be recovered. */
  logout: () => unknown;
}

/** Error shape produced by `customErrors` below — keeps the GraphQL error array intact. */
export type GqlBaseQueryError = {
  name: string;
  message: string;
  stack?: string;
  status?: number;
  errors?: Array<{ message?: string; extensions?: { code?: string } | null }>;
};

/**
 * GraphQL transports auth failures in a 200 body, so the session can only be
 * detected as dead by inspecting the error payload. `UNAUTHENTICATED` is the
 * authoritative signal; the message checks cover older/edge responses.
 */
function isUnauthenticated(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const err = error as GqlBaseQueryError;

  if (err.status === 401) return true;
  if (err.errors?.some((e) => e?.extensions?.code === 'UNAUTHENTICATED')) return true;

  const msg = (err.errors?.[0]?.message ?? err.message ?? '').toLowerCase();
  return (
    msg.includes('unauthorized') ||
    msg.includes('jwt expired') ||
    msg.includes('jwt malformed')
  );
}

/**
 * GraphQL `createApi` with bearer auth and a single-retry refresh flow.
 * Both the tenant app and the system admin app differ only in endpoint,
 * refresh mutation, and tag list.
 */
export function createGraphqlBaseApi<
  ReducerPath extends string,
  TagType extends string,
>({
  url,
  reducerPath,
  tagTypes,
  refreshMutation,
  refreshPayloadKey,
  getTokens,
  setTokens,
  logout,
}: CreateGraphqlBaseApiOptions<ReducerPath, TagType>) {
  const rawBaseQuery = graphqlRequestBaseQuery<GqlBaseQueryError>({
    url,
    prepareHeaders: (headers, { getState }) => {
      const token = getTokens(getState()).accessToken;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
    // The default drops everything but name/message/stack, discarding the
    // `extensions.code` needed to recognize an expired session.
    customErrors: ({ name, message, stack, response }) => ({
      name,
      message,
      stack,
      status: response?.status,
      errors: response?.errors,
    }),
  });

  /**
   * Shared across callers so a screen firing several queries at once triggers a
   * single refresh instead of one per request (which would race on token rotation).
   */
  let refreshInFlight: Promise<boolean> | null = null;

  const baseQueryWithReauth: BaseQueryFn<GqlArgs, unknown, unknown> = async (
    args,
    api,
    extraOptions,
  ) => {
    const result = await rawBaseQuery(args, api, extraOptions);

    if (!result.error || !isUnauthenticated(result.error)) return result;

    const runRefresh = async (): Promise<boolean> => {
      const refreshToken = getTokens(api.getState()).refreshToken;
      if (!refreshToken) {
        api.dispatch(logout() as never);
        return false;
      }

      const refreshResult = await rawBaseQuery(
        { document: refreshMutation, variables: { refreshToken } },
        api,
        extraOptions,
      );

      const payload = (
        refreshResult.data as
          | Record<string, { accessToken?: string; refreshToken?: string } | undefined>
          | undefined
      )?.[refreshPayloadKey];

      if (payload?.accessToken && payload?.refreshToken) {
        api.dispatch(
          setTokens({
            accessToken: payload.accessToken,
            refreshToken: payload.refreshToken,
          }) as never,
        );
        return true;
      }

      // End the session only when the refresh token itself was rejected. A network
      // blip or a 500 must leave the user signed in so the next attempt can recover.
      if (!refreshResult.error || isUnauthenticated(refreshResult.error)) {
        api.dispatch(logout() as never);
      }
      return false;
    };

    refreshInFlight ??= runRefresh().finally(() => {
      refreshInFlight = null;
    });

    const refreshed = await refreshInFlight;
    if (!refreshed) return result;

    return rawBaseQuery(args, api, extraOptions);
  };

  return createApi({
    reducerPath,
    baseQuery: baseQueryWithReauth,
    tagTypes: tagTypes as unknown as TagType[],
    endpoints: () => ({}),
  });
}
